import * as React from 'react';
import { cn } from '@noor/lib/cn';

/**
 * The five Eats MOBILE tab-bar glyphs, exported from the Figma mobile design
 * (node 26264:8934, "Tab bar") and cleaned to `currentColor` fills so each
 * inherits its color from `BottomNav.Item`'s state (brand orange when active,
 * muted gray otherwise).
 *
 * Deliberately a SEPARATE set from `eats-nav-icons.tsx`: that file draws the
 * desktop top-nav's marks (also reused by Delivery's own nav), and the mobile
 * tab bar uses genuinely different source instances for the same five
 * concepts — a filled home glyph instead of a bar chart for "dashboard", a
 * location pin instead of a folder for "menu", and so on. Mapping one concept
 * to two different marks is a Figma source-of-truth call, not a rendering
 * shortcut, so it is modeled as two icon sets rather than one shared shape
 * stretched to fit both designs.
 *
 * 24×24 source; each path keeps its native aspect and sits at the design's
 * exact inset within the box (not centered/stretched to fill).
 */
type IconProps = React.ComponentProps<'svg'>;

/** Dashboard tab ("Главное") — a filled house (home-5-fill). */
function NavMobileDashboardIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        transform="translate(3, 2.267)"
        d="M18 17.7327C18 17.9979 17.8946 18.2523 17.7071 18.4398C17.5196 18.6273 17.2652 18.7327 17 18.7327H1C0.734784 18.7327 0.48043 18.6273 0.292893 18.4398C0.105357 18.2523 0 17.9979 0 17.7327V7.22269C-0.000105484 7.07031 0.0346172 6.91991 0.101516 6.78299C0.168415 6.64608 0.26572 6.52626 0.386 6.43269L8.386 0.210694C8.56154 0.0741392 8.7776 0 9 0C9.2224 0 9.43846 0.0741392 9.614 0.210694L17.614 6.43269C17.7343 6.52626 17.8316 6.64608 17.8985 6.78299C17.9654 6.91991 18.0001 7.07031 18 7.22269V17.7327V17.7327ZM8 10.7327V16.7327H10V10.7327H8Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Menu tab ("Меню") — a location pin (Figma names it "Location Icon"). */
function NavMobileMenuIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        transform="translate(2, 1.409)"
        d="M20.3745 18.032C20.6429 18.3158 20.7899 18.6931 20.7844 19.0837C20.7789 19.4743 20.6212 19.8473 20.345 20.1234C20.0687 20.3995 19.6956 20.557 19.3051 20.5624C18.9145 20.5677 18.5373 20.4205 18.2535 20.152L13.6575 15.556L10.1215 19.092L2.34354 11.314C0.873617 9.84428 0.0334555 7.86014 0.000977718 5.78175C-0.0315 3.70336 0.746256 1.69393 2.16954 0.179L2.34354 0L20.3745 18.032Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Orders tab ("Заказы") — a document with a pull-tab (unnamed in Figma). */
function NavMobileOrdersIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <g transform="translate(2, 1.5)" fill="currentColor">
        <path d="M19 2.5C19 3.88071 17.8807 5 16.5 5C15.1193 5 14 3.88071 14 2.5C14 1.11929 15.1193 0 16.5 0C17.8807 0 19 1.11929 19 2.5Z" />
        <path d="M5 10.5C5 11.8807 3.88071 13 2.5 13C1.11929 13 0 11.8807 0 10.5C0 9.11929 1.11929 8 2.5 8C3.88071 8 5 9.11929 5 10.5Z" />
        <path d="M16.5 13C17.8807 13 19 11.8807 19 10.5C19 9.11929 17.8807 8 16.5 8C15.1193 8 14 9.11929 14 10.5C14 11.8807 15.1193 13 16.5 13Z" />
        <path d="M19 18.5C19 19.8807 17.8807 21 16.5 21C15.1193 21 14 19.8807 14 18.5C14 17.1193 15.1193 16 16.5 16C17.8807 16 19 17.1193 19 18.5Z" />
        <path d="M6.44152 11.1848C6.41375 11.3458 6.53303 11.5 6.69639 11.5H7.24991C7.38798 11.5 7.49991 11.6119 7.49991 11.75V15.5C7.49991 17.7091 9.29077 19.5 11.4999 19.5H12.3034C12.4668 19.5 12.5861 19.3458 12.5583 19.1848C12.5199 18.9623 12.4999 18.7335 12.4999 18.5C12.4999 18.2665 12.5199 18.0377 12.5583 17.8152C12.5861 17.6542 12.4668 17.5 12.3034 17.5H11.4999C10.3953 17.5 9.49991 16.6046 9.49991 15.5V11.75C9.49991 11.6119 9.61184 11.5 9.74991 11.5H12.3034C12.4668 11.5 12.5861 11.3458 12.5583 11.1848C12.5199 10.9623 12.4999 10.7335 12.4999 10.5C12.4999 10.2665 12.5199 10.0377 12.5583 9.81523C12.5861 9.65425 12.4668 9.5 12.3034 9.5H9.74991C9.61184 9.5 9.49991 9.38807 9.49991 9.25V5.5C9.49991 4.39543 10.3953 3.5 11.4999 3.5H12.3034C12.4668 3.5 12.5861 3.34575 12.5583 3.18477C12.5199 2.96227 12.4999 2.73347 12.4999 2.5C12.4999 2.26653 12.5199 2.03773 12.5583 1.81523C12.5861 1.65425 12.4668 1.5 12.3034 1.5H11.4999C9.29077 1.5 7.49991 3.29086 7.49991 5.5V9.25C7.49991 9.38807 7.38798 9.5 7.24991 9.5H6.69639C6.53303 9.5 6.41375 9.65425 6.44152 9.81523C6.47991 10.0377 6.49991 10.2665 6.49991 10.5C6.49991 10.7335 6.47991 10.9623 6.44152 11.1848Z" />
      </g>
    </svg>
  );
}

/** Venue tab ("Заведение") — a rounded arch (Figma names it "Reports Icon"). */
function NavMobileVenueIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        transform="translate(0.996, 2.004)"
        d="M16.0038 18.9956H6.00378C4.58701 18.9965 3.21563 18.496 2.13247 17.5827C1.04931 16.6695 0.324252 15.4024 0.0856863 14.0059C-0.152879 12.6093 0.110439 11.1734 0.829013 9.95242C1.54759 8.7314 2.67506 7.80405 4.01178 7.33457C3.9665 6.38823 4.11379 5.44254 4.44474 4.5548C4.77569 3.66706 5.2834 2.85574 5.93713 2.16999C6.59085 1.48424 7.37698 0.938325 8.24788 0.565325C9.11879 0.192325 10.0564 0 11.0038 0C11.9512 0 12.8888 0.192325 13.7597 0.565325C14.6306 0.938325 15.4167 1.48424 16.0704 2.16999C16.7242 2.85574 17.2319 3.66706 17.5628 4.5548C17.8938 5.44254 18.0411 6.38823 17.9958 7.33457C19.3325 7.80405 20.46 8.7314 21.1785 9.95242C21.8971 11.1734 22.1604 12.6093 21.9219 14.0059C21.6833 15.4024 20.9583 16.6695 19.8751 17.5827C18.7919 18.496 17.4206 18.9965 16.0038 18.9956Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Users/staff tab ("Сотрудники") — three people (Figma names it "Employees Icon"). */
function NavMobileUsersIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <g transform="translate(2, 4)" fill="currentColor">
        <path d="M4.1072 5.86495C4.4829 3.90273 5.81438 2.28 7.59848 1.49991C7.07975 0.603258 6.11027 0 4.99988 0C3.34303 0 1.99988 1.34315 1.99988 3C1.99988 4.34598 2.88629 5.48493 4.1072 5.86495Z" />
        <path d="M4.08454 8.00949C1.80371 8.15898 0 10.0564 0 12.375C0 12.7202 0.279822 13 0.625 13H3.02185C3.60567 12.0881 4.41092 11.3317 5.36176 10.8064C4.71341 10.0173 4.2625 9.05984 4.08454 8.00949Z" />
        <path d="M16.9782 13H19.375C19.7202 13 20 12.7202 20 12.375C20 10.0563 18.1963 8.15897 15.9154 8.00949C15.7375 9.05981 15.2866 10.0173 14.6383 10.8064C15.5891 11.3316 16.3944 12.0881 16.9782 13Z" />
        <path d="M15.8928 5.86489C17.1136 5.48481 17.9999 4.34591 17.9999 3C17.9999 1.34315 16.6567 0 14.9999 0C13.8895 0 12.9201 0.603225 12.4013 1.49984C14.1855 2.27989 15.517 3.90264 15.8928 5.86489Z" />
        <path d="M14.95 16H5.05001C4.74624 16 4.49999 15.7761 4.5 15.5C4.50006 13.567 6.22379 12 8.35009 12H11.6501C13.7764 12 15.5001 13.567 15.5 15.5C15.5 15.7762 15.2538 16 14.95 16Z" />
        <path d="M14 7C14 9.20914 12.2091 11 10 11C7.79086 11 6 9.20914 6 7C6 4.79086 7.79086 3 10 3C12.2091 3 14 4.79086 14 7Z" />
      </g>
    </svg>
  );
}

export {
  NavMobileDashboardIcon,
  NavMobileMenuIcon,
  NavMobileOrdersIcon,
  NavMobileVenueIcon,
  NavMobileUsersIcon,
};
export type { IconProps as EatsMobileNavIconProps };
